import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';

import { i18next } from '../../../internationalization/i18next';
import { PrinterIcon } from '../icons';

const styles = require('./page-layout.css');

/** An abstracted layout function */
export type PageLayoutFn = (params: PageLayoutParams) => JSX.Element;

export interface PageLayoutParams {
  /** Page title */
  title?: string;

  /** Link items to be shown in the headers */
  headerLinks?: MenuLink[];
  subLinks?: MenuLink[];

  onClickPrint?(): void;
  onClickFilter?(): void;
  onClickRoles?(): void;
  /// Enhanced Recommendations filter options
  onClickCategories?(): void;
  onClickCompetencies?(): void;
  onClickPriorities?(): void;
  onClickResetAll?(): void;

  filterApplied?: boolean;
  roleSelected?: string;
  rolesAvailable?: boolean;
  /// Enhanced Recommendations filter options
  /// No requirement to enable/disable
  categoryFilterApplied?: boolean;
  competencyFilterApplied?: boolean;
  priorityFilterApplied?: boolean;

  langSelector?: JSX.Element;

  /** Elements of the primary content area */
  children?: React.ReactNode;
}

export interface MenuLink {
  label: string;
  isHighlighted: boolean;
  action?(): void;
  isDisabled?: boolean;
  Icon?: JSX.Element;
}

export type LayoutType = 'tabbedContentUi' | 'singlePageUi';

export type IconState = 'active' | 'inactive' | 'disabled';

/** Props for the component */
export interface PageLayoutProps extends PageLayoutParams {
  logoUrl: string;
  userFullName?: string;
  logout?(): void;
  layoutType?: LayoutType;
}

/**
 * Component implementing typical pages
 *
 * Pick various styles as needed to render the Admin UI differently from the main app
 */
/// FF2-436 Walkthrough support - set data-tour to label name
@observer
export class PageLayout extends React.Component<PageLayoutProps> {
  /** Renders the header */
  renderHeader() {
    const headerLinks = this.props.headerLinks || [];
    const links = headerLinks.map(hl => (
      <div
        className={classnames(styles.headerLink, hl.isHighlighted ? styles.selected : undefined)}
        onClick={hl.action}
        key={hl.label}
        data-tour={hl.label}
      >
        {hl.label}
      </div>
    ));
    return (
      <header className={styles.header}>
        <div className={classnames(styles.headerRow, styles.default)}>
          <div className={styles.headerLeft}>
            <img className={styles.logo} src={this.props.logoUrl} />
            {links}
          </div>
          <div className={styles.headerRight}>
            {this.props.langSelector}
            {this.props.userFullName}
            {this.props.logout && (
              <div className={styles.logout} onClick={this.props.logout}>
                {i18next.t('header.logout', '(Logout)')}
              </div>
            )}
          </div>
        </div>
        {this.props.title ? this.renderTitleAndSubnav() : undefined}
      </header>
    );
  }

  /// This routine controls whether Print/Roles Filter/People Filter display at all by testing for the presence of
  /// their respective callbacks.
  renderTitleAndSubnav() {
    const {
      onClickCategories,
      onClickCompetencies,
      onClickFilter,
      onClickPrint,
      onClickPriorities,
      onClickResetAll,
      onClickRoles,
      subLinks,
      title
    } = this.props;
    return (
      <div className={classnames(styles.headerRow)}>
        <div className={classnames(styles.headerLeft, styles.pageInfo)}>
          {title ? this.renderTitle() : undefined}
          {/* <div className={styles.subnav}> */}
          <nav>
            {/* Render subnav here */}
            {subLinks !== undefined
              ? subLinks.map(link => this.renderSubNav(link.label, link.action, link.isDisabled))
              : undefined}
          </nav>
        </div>
        <div className={classnames(styles.headerRight, styles.actionButtons)}>
          {onClickPrint ? this.renderPrintButton() : undefined}
          {// Only set for Recommendations page
          onClickResetAll ? this.renderResetAllButton() : undefined}
          {onClickRoles ? this.renderRolesButton() : undefined}
          {onClickCategories ? this.renderCategoriesButton() : undefined}
          {onClickCompetencies ? this.renderCompetenciesButton() : undefined}
          {onClickPriorities ? this.renderPrioritiesButton() : undefined}
          {onClickFilter ? this.renderFilterButton() : undefined}
        </div>
      </div>
    );
  }

  /// FF2-436 Walkthrough support - set data-tour to title
  renderSubNav(title: string, action?: () => void, disabled?: boolean) {
    return (
      <div
        className={
          disabled
            ? classnames(styles.subnavLink, styles.disabled)
            : action
            ? styles.subnavLink
            : classnames(styles.subnavLink, styles.selected)
        }
        onClick={action}
        key={title}
        data-tour={title}
      >
        {title}
      </div>
    );
  }

  renderTitle() {
    return <div className={classnames(styles.headerTitle)}>{this.props.title}</div>;
  }

  /** Renders the content body */
  renderBody() {
    const { layoutType } = this.props;
    const style = layoutType === 'tabbedContentUi' ? styles.tabbed : styles.body;
    return <section className={style}>{this.props.children}</section>;
  }

  // TODO: Import Print icon from Rich
  renderPrintButton(): JSX.Element {
    return <PrinterIcon size={'xsmall'} onClick={this.props.onClickPrint} />;
  }

  // TODO: Import filter icon from Rich
  /// FF2-436 Add data-tour attribute for walkthroughs
  renderRolesButton(): JSX.Element {
    const { onClickRoles, rolesAvailable, roleSelected } = this.props;
    let roleFilterState: IconState = 'inactive';
    if (!rolesAvailable) {
      roleFilterState = 'disabled';
    } else if (roleSelected) {
      roleFilterState = 'active';
    }
    return (
      <div className={styles.filterButton} onClick={onClickRoles} data-tour={'roles-filter'}>
        <div className={styles.filterIcon}>{this.renderFilterIcon(roleFilterState)}</div>
        {this.renderRolesButtonText(roleFilterState, roleSelected)}
      </div>
    );
  }

  renderRolesButtonText(state: IconState, roleSelected?: string): JSX.Element {
    let roleFilterStyle = styles.filterText;
    let roleFilterTitle = i18next.t('filters.roles.name', 'Roles');
    if (state === 'disabled') {
      roleFilterStyle = styles.filterDisabledText;
    } else if (state === 'active') {
      roleFilterStyle = styles.filterAppliedText;
      // TODO: translate role shortname before it gets here
      roleFilterTitle = roleSelected || i18next.t('filters.roles.selected', 'Roles Selected');
    }
    return <div className={roleFilterStyle}>{roleFilterTitle}</div>;
  }

  // TODO: Import filter icon from Rich
  renderFilterButton(): JSX.Element {
    return (
      <div className={styles.filterButton} onClick={this.props.onClickFilter} data-tour={'people-filter'}>
        <div className={styles.filterIcon}>
          {this.renderFilterIcon(this.props.filterApplied ? 'active' : 'inactive')}
        </div>
        {/* <PrinterIcon size={"xsmall"} onClick={this.props.onClickFilter} /> */}
        {this.props.filterApplied ? (
          <div className={styles.filterAppliedText}>{i18next.t('filters.people.selected', 'People Selected')}</div>
        ) : (
          <div className={styles.filterText}>{i18next.t('filters.people.name', 'People')}</div>
        )}
      </div>
    );
  }

  renderCategoriesButton(): JSX.Element {
    return (
      <div className={styles.filterButton} onClick={this.props.onClickCategories} data-tour={'category-filter'}>
        <div className={styles.filterIcon}>
          {this.renderFilterIcon(this.props.categoryFilterApplied ? 'active' : 'inactive')}
        </div>
        {this.props.categoryFilterApplied ? (
          <div className={styles.filterAppliedText}>
            {i18next.t('filters.recommendation-categories.selected', 'Types Selected')}
          </div>
        ) : (
          <div className={styles.filterText}>{i18next.t('filters.recommendation-categories.name', 'Type')}</div>
        )}
      </div>
    );
  }

  renderCompetenciesButton(): JSX.Element {
    return (
      <div className={styles.filterButton} onClick={this.props.onClickCompetencies} data-tour={'competency-filter'}>
        <div className={styles.filterIcon}>
          {this.renderFilterIcon(this.props.competencyFilterApplied ? 'active' : 'inactive')}
        </div>
        {this.props.competencyFilterApplied ? (
          <div className={styles.filterAppliedText}>
            {i18next.t('filters.recommendation-competencies.selected', 'Skill Areas Selected')}
          </div>
        ) : (
          <div className={styles.filterText}>{i18next.t('filters.recommendation-competencies.name', 'Skill Area')}</div>
        )}
      </div>
    );
  }

  renderPrioritiesButton(): JSX.Element {
    return (
      <div className={styles.filterButton} onClick={this.props.onClickPriorities} data-tour={'priority-filter'}>
        <div className={styles.filterIcon}>
          {this.renderFilterIcon(this.props.priorityFilterApplied ? 'active' : 'inactive')}
        </div>
        {this.props.priorityFilterApplied ? (
          <div className={styles.filterAppliedText}>
            {i18next.t('filters.recommendation-priorities.selected', 'Priorities Selected')}
          </div>
        ) : (
          <div className={styles.filterText}>{i18next.t('filters.recommendation-priorities.name', 'Priority')}</div>
        )}
      </div>
    );
  }

  renderResetAllButton(): JSX.Element | null {
    const anyFilterOn =
      this.props.filterApplied ||
      this.props.categoryFilterApplied ||
      this.props.competencyFilterApplied ||
      this.props.priorityFilterApplied ||
      this.props.roleSelected;
    if (anyFilterOn) {
      return (
        <div className={styles.filterButton} onClick={this.props.onClickResetAll} data-tour={'reset-filters'}>
          {/* <div className={styles.filterIcon}>{this.renderFilterIcon("active")}</div> */}
          <div className={styles.filterAppliedText}>{i18next.t('filters.reset-all', 'Reset All')}</div>
        </div>
      );
    } else {
      return null;
    }
  }

  renderFilterIcon(state: IconState): JSX.Element {
    const strokeColour = () => {
      switch (state) {
        case 'active':
          return '#e16e46';
        case 'inactive':
          return '#312f30';
        default:
          return '#cdcccc';
      }
    };
    return (
      <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23.95 23.14">
        <path
          d="M23.2,1.55a.75.75,0,0,0-.75-.8H1.5a.75.75,0,0,0-.75.8,11.26,11.26,0,0,0,9,10.22v9.88a.73.73,0,0,0,.44.67.72.72,0,0,0,.79-.1l3-2.5a.74.74,0,0,0,.27-.57V11.77A11.26,11.26,0,0,0,23.2,1.55Z"
          style={{
            fill: 'none',
            stroke: strokeColour(),
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeWidth: '1.5px'
          }}
        />
      </svg>
    );
  }

  renderFooter() {
    return (
      <footer>
        <ul>
          <li>
            <a href="https://www.skillseeker.io/wp-content/uploads/2021/04/Privacy-Notice.pdf">Privacy Notice</a>
          </li>
          <li>
            <a href="https://www.skillseeker.io/wp-content/uploads/2021/04/Terms-of-Use.pdf">Terms of Service</a>
          </li>
        </ul>
      </footer>
    );
  }

  /** Renders the page */
  render() {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.page}>
          {this.renderHeader()}
          {this.renderBody()}
        </div>
        {this.renderFooter()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/ui/common/page-layout/page-layout.tsx